import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import usersManagementService from "../../services/usersManagementService";
import { getUserId } from "../../services/types/userPlanTypes";
import UsersLayoutLoading from "./components/UsersLayoutLoading";
import UsersPageHeader from "./components/UsersPageHeader";
import DashboardView from "./components/DashboardView";
import UsersTable from "./components/UsersTable";
import AlertsView from "./components/AlertsView";
import UserDetailDrawer from "./components/UserDetailDrawer";

const UsersLayout = () => {
  const [users, setUsers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeactivatedOnly, setShowDeactivatedOnly] = useState(false);
  const hasLoadedOnceRef = useRef(false);

  const loadUsers = useCallback(async () => {
    return usersManagementService.getUsers(
      showDeactivatedOnly ? { active: false } : {}
    );
  }, [showDeactivatedOnly]);

  const refreshTableUsers = useCallback(async () => {
    setTableLoading(true);
    try {
      const data = await loadUsers();
      setUsers(data);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
    } finally {
      setTableLoading(false);
    }
  }, [loadUsers]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (hasLoadedOnceRef.current) {
        setTableLoading(true);
      } else {
        setInitialLoading(true);
      }

      try {
        const data = await loadUsers();
        if (!cancelled) {
          setUsers(data);
          hasLoadedOnceRef.current = true;
        }
      } catch (err) {
        console.error("Error al cargar usuarios:", err);
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setTableLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [loadUsers]);

  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.active !== false).length,
      trial: users.filter((u) => u.status === "Prueba").length,
      expired: users.filter((u) => u.status === "Vencido").length,
    }),
    [users]
  );

  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.email.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm, users]
  );

  const handleTabChange = useCallback((_event, value) => {
    setActiveTab(value);
  }, []);

  const handleSelectUser = useCallback(async (user) => {
    setSelectedUser(user);
    setLoadingUserDetail(true);
    try {
      const detail = await usersManagementService.getUserWithPlan(user);
      setSelectedUser(detail);
    } catch (err) {
      console.error("Error al cargar plan del usuario:", err);
    } finally {
      setLoadingUserDetail(false);
    }
  }, []);

  const handleCreateUser = useCallback(
    async (newUser) => {
      try {
        const created = await usersManagementService.createUser(newUser);
        if (!showDeactivatedOnly) {
          await refreshTableUsers();
        }
        return created;
      } catch (err) {
        console.error("Error al crear usuario:", err);
        throw err;
      }
    },
    [refreshTableUsers, showDeactivatedOnly]
  );

  const handleUpdateUser = useCallback(async (user, overrides = {}) => {
    try {
      const saved = await usersManagementService.updateUser(user, overrides);
      const detail = await usersManagementService.getUserWithPlan({
        ...user,
        ...saved,
      });
      setUsers((prev) =>
        prev.map((u) => (getUserId(u) === getUserId(detail) ? { ...u, ...detail } : u))
      );
      setSelectedUser(detail);
      return detail;
    } catch (err) {
      console.error("Error al actualizar usuario:", err);
      throw err;
    }
  }, []);

  const handleDeactivateUser = useCallback(
    async (userId) => {
      try {
        await usersManagementService.deactivateUser(userId);
        setSelectedUser(null);

        if (showDeactivatedOnly) {
          await refreshTableUsers();
        } else {
          setUsers((prev) =>
            prev.filter((u) => getUserId(u) !== Number(userId) && getUserId(u) !== userId)
          );
        }

        return true;
      } catch (err) {
        console.error("Error al dar de baja usuario:", err);
        throw err;
      }
    },
    [refreshTableUsers, showDeactivatedOnly]
  );

  const handleCreateUserPlan = useCallback(async (createPayload) => {
    try {
      const created = await usersManagementService.createUserPlan(createPayload);
      setUsers((prev) => prev.map((u) => (u.id === created.id ? created : u)));
      setSelectedUser((prev) => (prev?.id === created.id ? created : prev));
      return created;
    } catch (err) {
      console.error("Error al crear plan de usuario:", err);
      throw err;
    }
  }, []);

  const handleRenewUserPlan = useCallback(async (user, overrides = {}) => {
    try {
      await usersManagementService.renewUserPlan(user, overrides);
      const detail = await usersManagementService.getUserWithPlan({
        ...user,
        company: user.company,
        email: user.email,
      });
      setUsers((prev) =>
        prev.map((u) => (getUserId(u) === getUserId(detail) ? { ...u, ...detail } : u))
      );
      setSelectedUser(detail);
      return detail;
    } catch (err) {
      console.error("Error al renovar plan de usuario:", err);
      throw err;
    }
  }, []);

  if (initialLoading) {
    return <UsersLayoutLoading />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container>
        <UsersPageHeader activeTab={activeTab} onTabChange={handleTabChange} />
      </Grid>

      {activeTab === "dashboard" && (
        <DashboardView
          stats={stats}
          users={users}
          setActiveTab={setActiveTab}
          onSelectUser={handleSelectUser}
        />
      )}
      {activeTab === "users" && (
        <UsersTable
          users={filteredUsers}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showDeactivatedOnly={showDeactivatedOnly}
          onShowDeactivatedOnlyChange={setShowDeactivatedOnly}
          tableLoading={tableLoading}
          onSelectUser={handleSelectUser}
          onCreateUser={handleCreateUser}
        />
      )}
      {activeTab === "alerts" && (
        <AlertsView
          users={users}
          setActiveTab={setActiveTab}
          onSelectUser={handleSelectUser}
        />
      )}

      <UserDetailDrawer
        user={selectedUser}
        loadingDetail={loadingUserDetail}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        onUpdateUser={handleUpdateUser}
        onUpdateUserPlan={handleRenewUserPlan}
        onDeactivateUser={handleDeactivateUser}
        onCreateUserPlan={handleCreateUserPlan}
        onRenewUserPlan={handleRenewUserPlan}
      />
    </Box>
  );
};

export default UsersLayout;
